import {makeAutoObservable} from 'mobx';
import {FEATURES, NOT_BETA_FEATURES_MAP, IPlanFeature, FEATURES_WITH_QUOTAS} from '../models/Plan';
import {ISaveable} from './types';
import { getNullableChangesOrModel, toNullableNumber } from '../models/model-types-methods';


export interface IFeatureChanges extends Partial<IPlanFeature> {
}

class FeatureStore implements ISaveable {
    private _model?: IPlanFeature;
    private _changes: IFeatureChanges = {};
    private _disabledAndOff: boolean = false;

    constructor(private _featureId: FEATURES, model?: IPlanFeature) {
        makeAutoObservable(this);
        if (model) {
            this._model = ensureValidModel(model) as IPlanFeature;
        }
    }

    public get disabled(): boolean { return this._disabledAndOff; }
    
    public setDisabled(v: boolean): void {
        this._disabledAndOff = v;
    }

    public setChanges(changes: IFeatureChanges) {
        this._changes = changes;
        return this;
    }

    get id(): number | undefined {
        return this._model?.id;
    }

    get feature_id(): number {
        return this._featureId;
    }

    get name(): string {
        return NOT_BETA_FEATURES_MAP[this._featureId]?.label ?? '';
    }

    get quota(): number|null|undefined {
        return getNullableChangesOrModel(this._changes.quota, this._model?.quota);
    }

    get isEnabled(): boolean {
        return this._changes._isEnabled ?? this._model?._isEnabled ?? false;
    }

    get pricePerExcess(): number|null {
        return getNullableChangesOrModel(this._changes.price_per_excess_usage, this._model?.price_per_excess_usage)
    }

    /** quota based feature (Users) should be enabled/disabled automatically according to quota */
    /** note: this setter is reused for usage based features like order tracking */
    setQuota(value: number|null) {
        this._changes.quota = value;
        if (FEATURES_WITH_QUOTAS[this._featureId]) {
            this._changes._isEnabled = (value ?? 0) > 0;
        }
    }

    setPricePerExcess(value: number) {
        this._changes._isEnabled = true;
        this._changes.price_per_excess_usage = value;
    }

    setIsEnabled(value: boolean) {
        if (FEATURES_WITH_QUOTAS[this._featureId]) {
            return this.setQuota(value
                ? FEATURES_WITH_QUOTAS[this._featureId].defaultQuota
                : null
            );
        }

        this._changes._isEnabled = value;
        if (value === this._model?._isEnabled) {
            delete this._changes._isEnabled;
        }
    }

    /** ISaveable implemetation */
    public discardChanges(): void {
        this._changes = {};
    }

    public getUpdatedModel(): IPlanFeature {
        return { feature_id: this._featureId, ...this._model, ...this._changes } as IPlanFeature;
    }

    hasChanges(): boolean {
        return !!Object.keys(this._changes).length;
    }

    save(): Promise<void> {
        throw new Error('not implemented');
    }

    isSaving(): boolean {
        throw new Error('not implemented');
    }
}

function ensureValidModel(model: IFeatureChanges): IFeatureChanges {
    return {
        ...model,
        quota: toNullableNumber(model.quota),
        price_per_excess_usage: toNullableNumber(model.price_per_excess_usage),
    }
}

export default FeatureStore;
